import { __assign, __awaiter, __extends, __generator } from "tslib";
import * as React from "react";
import { Platform, StyleSheet, Animated, BackHandler, Dimensions, Easing, I18nManager, TouchableWithoutFeedback, View, ScrollView, findNodeHandle } from "react-native";
import defaultTheme from "../styles/defaultTheme";
import Surface from "../Surface";
import styles from "./PnmacMenu.styles.js";
import PnmacMenuItem, { PnmacMenuItem as _MenuItem } from "./PnmacMenuItem";
import { APPROX_STATUSBAR_HEIGHT } from "../constants";
var SCREEN_INDENT = 8;
var ANIMATION_DURATION = 250;
var EASING = Easing.bezier(0.4, 0, 0.2, 1);
/**
 * Menus display a list of choices on temporary elevated surfaces. Their placement varies based on the element that opens them.
 *
 * ## Usage
 * ```js
 * import * as React from 'react';
 * import { View } from 'react-native';
 * import { PnmacButton, PnmacParagraph, PnmacMenu, Divider, PnmacProvider } from 'pnmac-universal-components';
 *
 * export default class MyComponent extends React.Component {
 *   state = {
 *     visible: false,
 *   };
 *
 *   _openMenu = () => this.setState({ visible: true });
 *
 *   _closeMenu = () => this.setState({ visible: false });
 *
 *   render() {
 *     return (
 *
 *         <View
 *           style={{
 *             paddingTop: 50,
 *             flexDirection: 'row',
 *             justifyContent: 'center'
 *           }}>
 *           <Menu
 *             visible={this.state.visible}
 *             onDismiss={this._closeMenu}
 *             anchor={
 *               <Button onPress={this._openMenu}>Show menu</Button>
 *             }
 *           >
 *             <Menu.Item onPress={() => {}} title="Item 1" />
 *             <Menu.Item onPress={() => {}} title="Item 2" />
 *             <Divider />
 *             <Menu.Item onPress={() => {}} title="Item 3" />
 *           </Menu>
 *         </View>
 *
 *     );
 *   }
 * }
 * ```
 */
var PnmacMenu = /** @class */function (_super) {
  __extends(PnmacMenu, _super);
  function PnmacMenu() {
    var _this = _super !== null && _super.apply(this, arguments) || this;
    _this.state = {
      rendered: _this.props.visible,
      top: 0,
      left: 0,
      menuLayout: {
        width: 0,
        height: 0
      },
      anchorLayout: {
        width: 0,
        height: 0
      },
      opacityAnimation: new Animated.Value(0),
      scaleAnimation: new Animated.ValueXY({
        x: 0,
        y: 0
      })
    };
    _this.subsc1 = null;
    _this.subsc2 = null;
    _this.subsc3 = null;
    _this.anchor = null;
    _this.menu = null;
    _this.isAnchorCoord = function () {
      return ! /*#__PURE__*/React.isValidElement(_this.props.anchor);
    };
    _this.measureMenuLayout = function () {
      return new Promise(function (resolve) {
        if (_this.menu) {
          _this.menu.measureInWindow(function (x, y, width, height) {
            resolve({
              x: x,
              y: y,
              width: width,
              height: height
            });
          });
        }
      });
    };
    _this.measureAnchorLayout = function () {
      return new Promise(function (resolve) {
        var anchor = _this.props.anchor;
        if (_this.isAnchorCoord()) {
          // @ts-ignore
          resolve({
            x: anchor.x,
            y: anchor.y,
            width: 0,
            height: 0
          });
          return;
        }
        if (_this.anchor) {
          _this.anchor.measureInWindow(function (x, y, width, height) {
            resolve({
              x: x,
              y: y,
              width: width,
              height: height
            });
          });
        }
      });
    };
    _this.updateVisibility = function () {
      return __awaiter(_this, void 0, void 0, function () {
        return __generator(this, function (_a) {
          switch (_a.label) {
            case 0:
              // Menu is rendered in Portal, which updates items asynchronously
              // We need to do the same here so that the ref is up-to-date
              return [4 /*yield*/, Promise.resolve()];
            case 1:
              // Menu is rendered in Portal, which updates items asynchronously
              // We need to do the same here so that the ref is up-to-date
              _a.sent();
              if (this.props.visible) {
                this.show();
              } else {
                this.hide();
              }
              return [2 /*return*/];
          }
        });
      });
    };
    _this.isBrowser = function () {
      return Platform.OS === "web" && "document" in global;
    };
    _this.focusFirstDOMNode = function (el) {
      if (el && _this.isBrowser()) {
        // When in the browser, we want to focus the first focusable item on toggle
        // For example, when menu is shown, focus the first item in the menu
        // And when menu is dismissed, send focus back to the button to resume tabbing
        var node = findNodeHandle(el);
        var focusableNode = node.querySelector(
        // This is a rough list of selectors that can be focused
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (focusableNode) {
          focusableNode.focus();
        }
      }
    };
    _this.handleDismiss = function () {
      if (_this.props.visible) {
        _this.props.onDismiss();
      }
      return true;
    };
    _this.handleKeypress = function (e) {
      if (e.key === "Escape") {
        _this.props.onDismiss();
      }
    };
    _this.attachListeners = function () {
      _this.subsc1 = BackHandler.addEventListener("hardwareBackPress", _this.handleDismiss);
      _this.subsc2 = Dimensions.addEventListener("change", _this.handleDismiss);
      if (_this.isBrowser()) {
        _this.subsc3 = Dimensions.addEventListener("keyup", _this.handleKeypress);
      }
    };
    _this.removeListeners = function () {
      var _a, _b, _c;
      (_a = _this.subsc1) === null || _a === void 0 ? void 0 : _a.remove();
      (_b = _this.subsc2) === null || _b === void 0 ? void 0 : _b.remove();
      _this.isBrowser() && ((_c = _this.subsc3) === null || _c === void 0 ? void 0 : _c.remove());
    };
    _this.show = function () {
      return __awaiter(_this, void 0, void 0, function () {
        var windowLayout, _a, menuLayout, anchorLayout;
        var _this = this;
        return __generator(this, function (_b) {
          switch (_b.label) {
            case 0:
              windowLayout = Dimensions.get("window");
              return [4 /*yield*/, Promise.all([this.measureMenuLayout(), this.measureAnchorLayout()])];
            case 1:
              _a = _b.sent(), menuLayout = _a[0], anchorLayout = _a[1];
              // When visible is true for first render
              // native views can be still not rendered and
              // measureMenuLayout/measureAnchorLayout functions
              // return wrong values e.g { x:0, y: 0, width: 0, height: 0 }
              // so we have to wait until views are ready
              // and rerun this function to show menu
              if (!windowLayout.width || !windowLayout.height || !menuLayout.width || !menuLayout.height || !anchorLayout.width && !this.isAnchorCoord() || !anchorLayout.height && !this.isAnchorCoord()) {
                requestAnimationFrame(this.show);
                return [2 /*return*/];
              }
              this.setState(function () {
                return {
                  left: anchorLayout.x,
                  top: anchorLayout.y,
                  anchorLayout: {
                    height: anchorLayout.height,
                    width: anchorLayout.width
                  },
                  menuLayout: {
                    width: menuLayout.width,
                    height: menuLayout.height
                  }
                };
              }, function () {
                _this.attachListeners();
                var animation = _this.props.theme.animation;
                Animated.parallel([Animated.timing(_this.state.scaleAnimation, {
                  toValue: {
                    x: menuLayout.width,
                    y: menuLayout.height
                  },
                  duration: ANIMATION_DURATION * animation.scale,
                  easing: EASING,
                  useNativeDriver: true
                }), Animated.timing(_this.state.opacityAnimation, {
                  toValue: 1,
                  duration: ANIMATION_DURATION * animation.scale,
                  easing: EASING,
                  useNativeDriver: true
                })]).start(function (_a) {
                  var finished = _a.finished;
                  if (finished) {
                    _this.focusFirstDOMNode(_this.menu);
                  }
                });
              });
              return [2 /*return*/];
          }
        });
      });
    };
    _this.hide = function () {
      _this.removeListeners();
      var animation = _this.props.theme.animation;
      Animated.timing(_this.state.opacityAnimation, {
        toValue: 0,
        duration: ANIMATION_DURATION * animation.scale,
        easing: EASING,
        useNativeDriver: true
      }).start(function (finished) {
        if (finished) {
          _this.setState({
            menuLayout: {
              width: 0,
              height: 0
            },
            rendered: false
          });
          _this.state.scaleAnimation.setValue({
            x: 0,
            y: 0
          });
          _this.focusFirstDOMNode(_this.anchor);
        }
      });
    };
    return _this;
  }
  PnmacMenu.getDerivedStateFromProps = function (nextProps, prevState) {
    if (nextProps.visible && !prevState.rendered) {
      return {
        rendered: true
      };
    }
    return null;
  };
  PnmacMenu.prototype.componentDidUpdate = function (prevProps) {
    if (prevProps.visible !== this.props.visible) {
      this.updateVisibility();
    }
  };
  PnmacMenu.prototype.componentWillUnmount = function () {
    this.removeListeners();
  };
  PnmacMenu.prototype.render = function () {
    var _this = this;
    var _a = this.props,
      visible = _a.visible,
      anchor = _a.anchor,
      contentStyle = _a.contentStyle,
      style = _a.style,
      children = _a.children,
      theme = _a.theme,
      statusBarHeight = _a.statusBarHeight,
      onDismiss = _a.onDismiss;
    var _b = this.state,
      rendered = _b.rendered,
      menuLayout = _b.menuLayout,
      anchorLayout = _b.anchorLayout,
      opacityAnimation = _b.opacityAnimation,
      scaleAnimation = _b.scaleAnimation;
    var _c = this.state,
      left = _c.left,
      top = _c.top;
    // I don't know why but on Android measure function is wrong by 24
    var additionalVerticalValue = Platform.select({
      android: statusBarHeight,
      default: 0
    });
    var scaleTransforms = [{
      scaleX: scaleAnimation.x.interpolate({
        inputRange: [0, menuLayout.width],
        outputRange: [0, 1]
      })
    }, {
      scaleY: scaleAnimation.y.interpolate({
        inputRange: [0, menuLayout.height],
        outputRange: [0, 1]
      })
    }];
    var windowLayout = Dimensions.get("window");
    // We need to translate menu while animating scale to imitate transform origin for scale animation
    var positionTransforms = [];
    // Check if menu fits horizontally and if not align it to right.
    if (left <= windowLayout.width - menuLayout.width - SCREEN_INDENT) {
      positionTransforms.push({
        translateX: scaleAnimation.x.interpolate({
          inputRange: [0, menuLayout.width],
          outputRange: [-(menuLayout.width / 2), 0]
        })
      });
      // Check if menu position has enough space from left side
      if (left < SCREEN_INDENT) {
        left = SCREEN_INDENT;
      }
    } else {
      positionTransforms.push({
        translateX: scaleAnimation.x.interpolate({
          inputRange: [0, menuLayout.width],
          outputRange: [menuLayout.width / 2, 0]
        })
      });
      left += anchorLayout.width - menuLayout.width;
      var right = left + menuLayout.width;
      // Check if menu position has enough space from right side
      if (right > windowLayout.width - SCREEN_INDENT) {
        left = windowLayout.width - SCREEN_INDENT - menuLayout.width;
      }
    }
    // If the menu is larger than available vertical space,
    // calculate the height of scrollable view
    var scrollableMenuHeight = 0;
    // Check if the menu should be scrollable
    if (
    // Check if the menu overflows from bottom side
    top >= windowLayout.height - menuLayout.height - SCREEN_INDENT - additionalVerticalValue &&
    // And bottom side of the screen has more space than top side
    top <= windowLayout.height - top) {
      // Scrollable menu should be below the anchor (expands downwards)
      scrollableMenuHeight = windowLayout.height - top - SCREEN_INDENT - additionalVerticalValue;
    } else if (
    // Check if the menu overflows from bottom side
    top >= windowLayout.height - menuLayout.height - SCREEN_INDENT - additionalVerticalValue &&
    // And top side of the screen has more space than bottom side
    top >= windowLayout.height - top &&
    // And menu overflows from top side
    top <= menuLayout.height - anchorLayout.height + SCREEN_INDENT - additionalVerticalValue) {
      // Scrollable menu should be above the anchor (expands upwards)
      scrollableMenuHeight = top + anchorLayout.height - SCREEN_INDENT + additionalVerticalValue;
    }
    // Scrollable menu max height
    scrollableMenuHeight = scrollableMenuHeight > windowLayout.height - 2 * SCREEN_INDENT ? windowLayout.height - 2 * SCREEN_INDENT : scrollableMenuHeight;
    // Menu is typically positioned below the element that generates it
    // So first check if it fits below the anchor (expands downwards)
    if (
    // Check if menu fits vertically
    top <= windowLayout.height - menuLayout.height - SCREEN_INDENT - additionalVerticalValue ||
    // Or if the menu overflows from bottom side
    top >= windowLayout.height - menuLayout.height - SCREEN_INDENT - additionalVerticalValue &&
    // And bottom side of the screen has more space than top side
    top <= windowLayout.height - top) {
      positionTransforms.push({
        translateY: scaleAnimation.y.interpolate({
          inputRange: [0, menuLayout.height],
          outputRange: [-((scrollableMenuHeight || menuLayout.height) / 2), 0]
        })
      });
      // Check if menu position has enough space from top side
      if (top < SCREEN_INDENT) {
        top = SCREEN_INDENT;
      }
    } else {
      positionTransforms.push({
        translateY: scaleAnimation.y.interpolate({
          inputRange: [0, menuLayout.height],
          outputRange: [(scrollableMenuHeight || menuLayout.height) / 2, 0]
        })
      });
      top += anchorLayout.height - (scrollableMenuHeight || menuLayout.height);
      var bottom = top + (scrollableMenuHeight || menuLayout.height) + additionalVerticalValue;
      // Check if menu position has enough space from bottom side
      if (bottom > windowLayout.height - SCREEN_INDENT) {
        top = scrollableMenuHeight === windowLayout.height - 2 * SCREEN_INDENT ? -SCREEN_INDENT * 2 : windowLayout.height - menuLayout.height - SCREEN_INDENT - additionalVerticalValue;
      }
    }
    var shadowMenuContainerStyle = __assign({
      opacity: opacityAnimation,
      transform: scaleTransforms,
      borderRadius: 10
    }, scrollableMenuHeight ? {
      height: scrollableMenuHeight
    } : {});
    var positionStyle = __assign({
      top: this.isAnchorCoord() ? top : top + additionalVerticalValue
    }, I18nManager.isRTL ? {
      right: left
    } : {
      left: left
    });
    return /*#__PURE__*/React.createElement(View, {
      ref: function (ref) {
        _this.anchor = ref;
      },
      style: __assign(__assign({}, styles.menuContainerStyles), this.props.menuContainerStyles),
      collapsable: false,
      testID: "menu"
    }, this.isAnchorCoord() ? null : anchor, rendered ? (/*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TouchableWithoutFeedback, {
      onPress: onDismiss
    }, /*#__PURE__*/React.createElement(View, {
      style: StyleSheet.absoluteFill
    })), /*#__PURE__*/React.createElement(View, {
      ref: function (ref) {
        _this.menu = ref;
      },
      collapsable: false,
      accessibilityViewIsModal: visible,
      style: [styles.wrapper, positionStyle, style],
      pointerEvents: visible ? "box-none" : "none"
    }, /*#__PURE__*/React.createElement(Animated.View, {
      style: {
        transform: positionTransforms
      }
    }, /*#__PURE__*/React.createElement(Surface, {
      style: [styles.shadowMenuContainer, shadowMenuContainerStyle, contentStyle]
    }, scrollableMenuHeight && (/*#__PURE__*/React.createElement(ScrollView, null, children)) || /*#__PURE__*/React.createElement(React.Fragment, null, children)))))) : null);
  };
  PnmacMenu.Item = PnmacMenuItem;
  PnmacMenu.defaultProps = {
    statusBarHeight: APPROX_STATUSBAR_HEIGHT
  };
  return PnmacMenu;
}(React.Component);
export default defaultTheme(PnmacMenu);